import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Eye, Phone, ClipboardList, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Compact 7d/30d stats panel for a single project. Owner-only backend. */
const StatsPanel = ({ projectId, getToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const token = await getToken();
      if (!token) throw new Error('You need to be logged in.');
      const { data } = await axios.get(`${API}/projects/${projectId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 12000,
      });
      setData(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err?.message || 'Could not load stats.'));
    } finally {
      setLoading(false);
    }
  }, [projectId, getToken]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const totalActivity = data
    ? data.last_30.views + data.last_30.call_taps + data.last_30.quote_requests
    : 0;
  const hasAnyData = totalActivity > 0;

  return (
    <div
      data-testid="stats-panel"
      className="mt-6 bg-white rounded-2xl border p-5 sm:p-6"
      style={{ borderColor: 'var(--yb-border)' }}
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>
            Site stats
          </p>
          <h3 className="text-[18px] font-semibold mt-0.5" style={{ color: 'var(--yb-ink)' }}>
            Is your site working?
          </h3>
        </div>
        <button
          type="button"
          onClick={() => { setLoading(true); load(); }}
          disabled={loading}
          data-testid="stats-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12.5px] bg-white disabled:opacity-60"
          style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : null}
          Refresh
        </button>
      </div>

      {error && (
        <p className="text-[13.5px]" style={{ color: '#B4530A' }} data-testid="stats-error">{error}</p>
      )}

      {loading && !data && (
        <p className="text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>Loading…</p>
      )}

      {data && !hasAnyData && (
        <div
          data-testid="stats-empty"
          className="rounded-xl border p-4"
          style={{ borderColor: 'var(--yb-border)', backgroundColor: '#F5F4F0' }}
        >
          <p className="text-[14px]" style={{ color: 'var(--yb-ink)' }}>
            Stats will appear here once your site gets visitors.
          </p>
          <p className="text-[12.5px] mt-1" style={{ color: 'var(--yb-muted)' }}>
            Share your published link with a mate, tap the Call button and submit the quote form —
            they'll show up right here.
          </p>
        </div>
      )}

      {data && hasAnyData && (
        <div className="space-y-4">
          <StatsRow label="Last 7 days" now={data.last_7} prev={data.prev_7} testid="stats-last-7" />
          <StatsRow label="Last 30 days" now={data.last_30} prev={data.prev_30} testid="stats-last-30" />
          <p className="text-[11.5px]" style={{ color: 'var(--yb-muted)' }}>
            Aggregate counts only — no visitor names, IPs, or personal data collected.
          </p>
        </div>
      )}
    </div>
  );
};

const METRICS = [
  { key: 'views', label: 'Views', Icon: Eye },
  { key: 'call_taps', label: 'Call taps', Icon: Phone },
  { key: 'quote_requests', label: 'Quote requests', Icon: ClipboardList },
];

const StatsRow = ({ label, now, prev, testid }) => (
  <div data-testid={testid}>
    <p className="text-[12px] uppercase tracking-wider mb-2" style={{ color: 'var(--yb-muted)' }}>
      {label}
    </p>
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {METRICS.map((m) => (
        <StatTile
          key={m.key}
          Icon={m.Icon}
          label={m.label}
          value={now[m.key] || 0}
          prev={prev[m.key] || 0}
          testid={`${testid}-${m.key}`}
        />
      ))}
    </div>
  </div>
);

const StatTile = ({ Icon, label, value, prev, testid }) => {
  const diff = value - prev;
  let trend = 'flat';
  if (diff > 0) trend = 'up';
  else if (diff < 0) trend = 'down';
  const trendColor = trend === 'up' ? '#16A34A' : trend === 'down' ? '#B4530A' : 'var(--yb-muted)';
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendLabel = trend === 'flat'
    ? 'same as before'
    : `${diff > 0 ? 'up' : 'down'} from ${prev}`;

  return (
    <div
      data-testid={testid}
      className="rounded-xl border p-3 sm:p-4 flex flex-col gap-1"
      style={{ borderColor: 'var(--yb-border)', backgroundColor: 'white' }}
    >
      <div className="flex items-center gap-1.5 text-[11.5px] uppercase tracking-wider" style={{ color: 'var(--yb-muted)' }}>
        <Icon size={12} />
        <span className="truncate">{label}</span>
      </div>
      <div className="text-[26px] sm:text-[30px] font-semibold leading-none" style={{ color: 'var(--yb-ink)' }}>
        {value}
      </div>
      <div className="flex items-center gap-1 text-[11.5px]" style={{ color: trendColor }}>
        <TrendIcon size={12} />
        <span>{trendLabel}</span>
      </div>
    </div>
  );
};

export default StatsPanel;
